<template>
  <AIVDialog
    v-model="isVisible"
    title="Admin Clock Out"
    width="40%"
    class="mobile-friendly-modal admin-clock-out-modal"
  >
    <div class="admin-clock-out-content">
      <p class="admin-clock-out-intro">
        <i class="fa-solid fa-circle-info"></i>
        Close this shift on behalf of the caregiver. Use this when a caregiver
        forgot to clock out. No shift notes are collected; the action is recorded
        in the shift history.
      </p>

      <div class="mb-3">
        <h5>Shift Details</h5>
        <p><strong>Date:</strong> {{ formattedDate }}</p>
        <p><strong>Scheduled Time:</strong> {{ scheduledRange }}</p>
        <p><strong>Caregiver:</strong> {{ caregiverLabel }}</p>
        <p v-if="clientLabel"><strong>Client:</strong> {{ clientLabel }}</p>
      </div>

      <!-- Group shift: which caregiver(s) to clock out -->
      <div v-if="isGroupShift && clockedInCaregivers.length > 0" class="mb-3">
        <label class="form-label">Caregiver to clock out:</label>
        <AIVSelect
          :value="selectedCaregiverId"
          @input="selectedCaregiverId = $event"
          :options="caregiverSelectOptions"
          placeholder="Select caregiver"
          :custom-style="{ minWidth: '100%' }"
        />
      </div>

      <div class="mb-3">
        <label class="form-label">Clock Out Time: <span class="required-indicator">*</span></label>
        <div class="time-input-container">
          <AIVTimeSelect
            v-model="clockOutTime"
            placeholder="Select clock out time"
            class="clock-time-picker"
            :allowExactMinutes="true"
          />
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Reason: <span class="required-indicator">*</span></label>
        <el-input
          v-model="reason"
          type="textarea"
          :rows="3"
          placeholder="Why are you clocking this shift out on the caregiver's behalf?"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          class="clock-out-btn"
          @click="confirmClockOut"
          :loading="submitting"
          :disabled="!clockOutTime || !reason.trim()"
        >
          <i class="fa-solid fa-clock"></i> Clock Out
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVTimeSelect from "@/components/AIVTimeSelect.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";

export default {
  name: "AdminClockOutModal",
  components: { AIVDialog, AIVTimeSelect, AIVSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      submitting: false,
      clockOutTime: null,
      reason: "",
      selectedCaregiverId: null,
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    orig() {
      return this.shift?.originalData || this.shift || {};
    },

    shiftId() {
      return this.orig.id || this.shift?.id;
    },

    shiftDate() {
      return this.shift?.date || this.orig.date;
    },

    isGroupShift() {
      const type = this.shift?.shift_type || this.shift?.shiftType || this.orig.shift_type;
      return type === "Group";
    },

    caregivers() {
      return this.shift?.caregivers || this.orig.caregivers || [];
    },

    caregiverClockTimes() {
      return this.shift?.caregiverClockTimes || this.orig.caregiverClockTimes || [];
    },

    // Group-shift caregivers who have clocked in but not yet out.
    clockedInCaregivers() {
      if (!this.isGroupShift) return [];
      const stillIn = this.caregiverClockTimes
        .filter((ct) => ct.clock_in && !ct.clock_out)
        .map((ct) => Number(ct.id_users));
      return this.caregivers.filter((c) => stillIn.includes(Number(c.id)));
    },

    caregiverSelectOptions() {
      const opts = this.clockedInCaregivers.map((c) => ({
        id: c.id,
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim() || `User #${c.id}`,
      }));
      if (this.clockedInCaregivers.length > 1) {
        opts.unshift({ id: null, name: "All clocked-in caregivers" });
      }
      return opts;
    },

    caregiverLabel() {
      const names = this.caregivers
        .map((c) => `${c.first_name || ""} ${c.last_name || ""}`.trim())
        .filter(Boolean);
      return names.length ? names.join(", ") : "Unassigned";
    },

    clientLabel() {
      const clients = this.shift?.clients || this.orig.clients || [];
      const names = clients
        .map((c) => c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim())
        .filter(Boolean);
      return names.join(", ");
    },

    formattedDate() {
      if (!this.shiftDate) return "";
      const dateStr = this.shiftDate.split("T")[0];
      const [y, m, d] = dateStr.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    scheduledRange() {
      const start = this.shift?.startTime || this.orig.scheduled_start;
      const end = this.shift?.endTime || this.orig.scheduled_end;
      return `${this.formatTime(start)} - ${this.formatTime(end)}`;
    },
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.reset();
        this.initDefaults();
      }
    },
  },

  methods: {
    reset() {
      this.clockOutTime = null;
      this.reason = "";
      this.selectedCaregiverId = null;
      this.submitting = false;
    },

    initDefaults() {
      // Default the clock-out time to the scheduled end time when available,
      // otherwise leave blank for the admin to choose.
      const end = this.shift?.endTime || this.orig.scheduled_end;
      this.clockOutTime = this.toLocalHHMM(end);
      // Pre-select the only clocked-in caregiver on a group shift.
      if (this.isGroupShift && this.clockedInCaregivers.length === 1) {
        this.selectedCaregiverId = this.clockedInCaregivers[0].id;
      }
    },

    // Accepts either a "HH:MM[:SS]" time-of-day or a full datetime string.
    toLocalHHMM(value) {
      if (!value) return null;
      if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
        const [h, m] = value.split(":");
        return `${String(Number(h)).padStart(2, "0")}:${m.slice(0, 2)}`;
      }
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return null;
      return dt.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatTime(value) {
      const hhmm = this.toLocalHHMM(value);
      if (!hhmm) return "";
      const [h, m] = hhmm.split(":").map(Number);
      const ampm = h >= 12 ? "PM" : "AM";
      const dh = h % 12 || 12;
      return `${dh}:${String(m).padStart(2, "0")} ${ampm}`;
    },

    handleCancel() {
      this.isVisible = false;
      this.reset();
    },

    async confirmClockOut() {
      if (!this.reason.trim()) {
        this.$notifyError("Please provide a reason.");
        return;
      }
      if (!this.clockOutTime) {
        this.$notifyError("Please select a clock-out time.");
        return;
      }
      if (!this.shiftId || !this.shiftDate) {
        this.$notifyError("Shift data is unavailable. Please try again.");
        return;
      }

      // Build a UTC ISO timestamp from the shift date + chosen local time.
      const dateStr = this.shiftDate.split("T")[0];
      const [year, month, day] = dateStr.split("-").map(Number);
      const [hours, minutes] = this.clockOutTime.split(":").map(Number);
      const localClockOut = new Date(year, month - 1, day, hours, minutes, 0, 0);

      const payload = {
        clockOutTime: localClockOut.toISOString(),
        reason: this.reason.trim(),
      };
      if (this.isGroupShift && this.selectedCaregiverId) {
        payload.caregiverId = this.selectedCaregiverId;
      }

      this.submitting = true;
      try {
        await shiftsService.adminClockOut(this.shiftId, payload);
        this.$notifySuccess("Shift clocked out.");
        this.isVisible = false;
        this.$emit("clocked-out", { shiftId: this.shiftId });
        this.reset();
      } catch (error) {
        this.$notifyError(
          "Error clocking out: " +
            (error.response?.data?.error ||
              error.response?.data?.message ||
              error.message),
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-clock-out-intro {
  background: rgba(64, 158, 255, 0.12);
  border-left: 3px solid #409eff;
  padding: 0.6rem 0.8rem;
  border-radius: 4px;
  font-size: 0.85rem;
  margin-bottom: 1rem;
}
.required-indicator {
  color: #f56c6c;
}
.time-input-container {
  width: 100%;
}
</style>
